import { useState, useEffect } from 'react';
import { Button, Form, Input, Tabs, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { $ } from '../lib/api';
import { useAuthStore } from '../store/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const accessToken = useAuthStore((s) => s.accessToken);
  const userInStore = useAuthStore((s) => s.user);
  const [tab, setTab] = useState('password');
  const [needs2fa, setNeeds2fa] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  // v1.5 修复：用 Form.useForm() 拿短信表单实例，从 form state 取手机号
  // 之前 document.querySelector('input[id$="phone"]') 在 2 个 Form 共存时抓到密码 tab 的 input → 永远空
  const [smsForm] = Form.useForm();

  // v1.2-rc2 #5：按 role 分流 — 教务角色去 /academic/dashboard，其他去 /dashboard
  const homeFor = (role?: string) =>
    role === 'academic' || role === 'academic_director' ? '/academic/dashboard' : '/dashboard';

  // Already-logged-in users hitting /login should bounce by role
  useEffect(() => {
    if (accessToken) navigate(homeFor(userInStore?.role), { replace: true });
  }, [accessToken, userInStore?.role, navigate]);

  const sendCode = async (phone: string, purpose: 'login' | '2fa') => {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) return message.warning('请输入手机号');
    await $.post('/auth/sms-code', { phone, purpose });
    message.success('验证码已发送到手机');
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const onLoginPwd = async (v: any) => {
    setSubmitting(true);
    try {
      const r: any = await $.post('/auth/login/password', v);
      if (r.requires2fa) { setNeeds2fa(true); message.info('请输入收到的短信验证码'); return; }
      setSession({ accessToken: r.accessToken, refreshToken: r.refreshToken, user: r.user, mustChangePassword: r.mustChangePassword });
      // #5：按 role 分流，academic 去 /academic/dashboard
      navigate(homeFor(r.user?.role), { replace: true });
    } finally { setSubmitting(false); }
  };

  const onLoginSms = async (v: any) => {
    setSubmitting(true);
    try {
      const r: any = await $.post('/auth/login/sms', v);
      setSession({ accessToken: r.accessToken, refreshToken: r.refreshToken, user: r.user, mustChangePassword: r.mustChangePassword });
      // #5：按 role 分流，academic 去 /academic/dashboard
      navigate(homeFor(r.user?.role), { replace: true });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">幻</div>
          <div>
            <div className="login-title">HuanKe-CRM</div>
            <div className="login-sub">教育销售团队 · AI 副驾驶</div>
          </div>
        </div>
        <Tabs activeKey={tab} onChange={setTab} items={[
          { key: 'password', label: '密码登录', children: (
            <Form layout="vertical" onFinish={onLoginPwd}>
              <Form.Item name="phone" label="手机号" rules={[{ required: true, pattern: /^1[3-9]\d{9}$/ }]}>
                <Input placeholder="11 位手机号" />
              </Form.Item>
              <Form.Item name="password" label="密码" rules={[{ required: true, min: 6 }]}>
                <Input.Password placeholder="≥ 8 位含数字字母" />
              </Form.Item>
              {needs2fa && (
                <Form.Item name="smsCode" label="短信验证码（双因素）" rules={[{ required: true }]}>
                  <Input placeholder="6 位验证码" />
                </Form.Item>
              )}
              <Button type="primary" htmlType="submit" loading={submitting} block>登录</Button>
            </Form>
          ) },
          { key: 'sms', label: '短信登录', children: (
            <Form form={smsForm} layout="vertical" onFinish={onLoginSms}>
              <Form.Item name="phone" label="手机号" rules={[{ required: true, pattern: /^1[3-9]\d{9}$/ }]}>
                <Input placeholder="11 位手机号" />
              </Form.Item>
              <Form.Item name="code" label="验证码" rules={[{ required: true, min: 4 }]}>
                <Input.Search
                  enterButton={countdown > 0 ? `${countdown}s` : '发送验证码'}
                  onSearch={async () => {
                    if (countdown > 0) return;
                    // v1.5 修复：从 Form 实例拿手机号，不靠 DOM querySelector
                    const phone = smsForm.getFieldValue('phone');
                    await sendCode(phone, 'login');
                  }}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} block>登录</Button>
            </Form>
          ) },
        ]} />
      </div>
    </div>
  );
}
